import React, { useState, useEffect } from 'react';
import { get, put } from '../../utils/apiClient';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminActivitiesPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/activities');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load activities page.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleHeaderTitleChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      header: {
        ...((prev && prev.header) || {}),
        title: value,
      },
    }));
  };

  const handleOverviewTextChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      overviewText: value,
    }));
  };

  const updateActivity = (sectionKey, index, field, value) => {
    setPageData((prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev[sectionKey]) ? [...prev[sectionKey]] : [];
      const existing = list[index] || {};
      list[index] = {
        ...existing,
        [field]: field === 'displayOrder' && value !== ''
          ? Number(value)
          : value === ''
            ? undefined
            : value,
      };
      return {
        ...prev,
        [sectionKey]: list,
      };
    });
  };

  const addActivity = (sectionKey) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const list = Array.isArray(next[sectionKey]) ? [...next[sectionKey]] : [];
      list.push({ title: '', day: '', time: '', description: '' });
      next[sectionKey] = list;
      return next;
    });
  };

  const removeActivity = (sectionKey, index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev[sectionKey])) return prev;
      const list = prev[sectionKey].filter((_, i) => i !== index);
      return {
        ...prev,
        [sectionKey]: list,
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title: (pageData.header && pageData.header.title) || 'Church Activities',
        },
        overviewText: pageData.overviewText || '',
        weeklyActivities: Array.isArray(pageData.weeklyActivities)
          ? pageData.weeklyActivities
          : [],
        monthlyActivities: Array.isArray(pageData.monthlyActivities)
          ? pageData.monthlyActivities
          : [],
        yearlyActivities: Array.isArray(pageData.yearlyActivities)
          ? pageData.yearlyActivities
          : [],
      };

      const updated = await put('/activities', payload);
      setPageData(updated);
      setSaveSuccess('Activities page saved successfully.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save activities page.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Church Activities CMS</AdminSectionTitle>
        <AdminSectionText>Loading activities page...</AdminSectionText>
      </>
    );
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const weeklyActivities = Array.isArray(pageData && pageData.weeklyActivities)
    ? pageData.weeklyActivities
    : [];
  const monthlyActivities = Array.isArray(pageData && pageData.monthlyActivities)
    ? pageData.monthlyActivities
    : [];
  const yearlyActivities = Array.isArray(pageData && pageData.yearlyActivities)
    ? pageData.yearlyActivities
    : [];

  const renderActivitiesEditor = (label, sectionKey, items) => (
    <div style={{ marginBottom: 16 }}>
      <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
        {label}
      </AdminSectionTitle>
      {(!items || items.length === 0) && (
        <AdminSectionText style={{ fontSize: '0.9rem' }}>
          No activities configured yet.
        </AdminSectionText>
      )}
      {items && items.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {items.map((item, index) => (
            <div
              key={`${item.title || 'activity'}-${index}`}
              style={{
                border: '1px solid #ddd',
                borderRadius: 8,
                padding: 10,
                marginBottom: 8,
                fontSize: '0.9rem',
              }}
            >
              <div style={{ marginBottom: 6 }}>
                <AdminLoginLabel htmlFor={`${sectionKey}-title-${index}`}>
                  Title
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`${sectionKey}-title-${index}`}
                  value={item.title || ''}
                  onChange={(e) => updateActivity(sectionKey, index, 'title', e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <AdminLoginLabel htmlFor={`${sectionKey}-day-${index}`}>
                    Day
                  </AdminLoginLabel>
                  <AdminLoginInput
                    id={`${sectionKey}-day-${index}`}
                    value={item.day || ''}
                    onChange={(e) => updateActivity(sectionKey, index, 'day', e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <AdminLoginLabel htmlFor={`${sectionKey}-time-${index}`}>
                    Time
                  </AdminLoginLabel>
                  <AdminLoginInput
                    id={`${sectionKey}-time-${index}`}
                    value={item.time || ''}
                    onChange={(e) => updateActivity(sectionKey, index, 'time', e.target.value)}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 6 }}>
                <AdminLoginLabel htmlFor={`${sectionKey}-description-${index}`}>
                  Description
                </AdminLoginLabel>
                <textarea
                  id={`${sectionKey}-description-${index}`}
                  rows={2}
                  value={item.description || ''}
                  onChange={(e) => updateActivity(sectionKey, index, 'description', e.target.value)}
                  style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
                />
              </div>
              <div style={{ marginBottom: 6 }}>
                <AdminLoginLabel htmlFor={`${sectionKey}-order-${index}`}>
                  Display Order (optional)
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`${sectionKey}-order-${index}`}
                  type="number"
                  value={
                    typeof item.displayOrder === 'number'
                      ? item.displayOrder
                      : ''
                  }
                  onChange={(e) =>
                    updateActivity(
                      sectionKey,
                      index,
                      'displayOrder',
                      e.target.value,
                    )
                  }
                />
              </div>
              <AdminButton type="button" onClick={() => removeActivity(sectionKey, index)}>
                Remove Activity
              </AdminButton>
            </div>
          ))}
        </div>
      )}
      <div>
        <AdminButton type="button" onClick={() => addActivity(sectionKey)}>
          Add Activity
        </AdminButton>
      </div>
    </div>
  );

  return (
    <>
      <AdminSectionTitle>Church Activities CMS</AdminSectionTitle>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="activities-header-title">Header Title</AdminLoginLabel>
          <AdminLoginInput
            id="activities-header-title"
            value={(header && header.title) || ''}
            onChange={handleHeaderTitleChange}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="activities-overview">Overview Text</AdminLoginLabel>
          <textarea
            id="activities-overview"
            rows={3}
            value={pageData.overviewText || ''}
            onChange={handleOverviewTextChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        {renderActivitiesEditor('Weekly Activities', 'weeklyActivities', weeklyActivities)}
        {renderActivitiesEditor('Monthly Activities', 'monthlyActivities', monthlyActivities)}
        {renderActivitiesEditor('Yearly Activities', 'yearlyActivities', yearlyActivities)}

        <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Activities Page'}
          </AdminSaveButton>
          {saveSuccess && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
          )}
          {error && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
          )}
        </div>
      </form>
    </>
  );
};

export default AdminActivitiesPage;
